import React, { useState, useMemo, useCallback } from 'react';
import { object, string } from 'yup';
import without from 'lodash/without';
import {
  Fade,
  Typography,
  Button,
  Banner,
  MinusIcon,
} from '@forgeglobal/design-framework';

import {
  Form,
  Field,
  FormProps,
  FormRenderProps,
  FieldRenderProps,
} from '@tcs/common/components/atoms/Form';
import TextField from '@tcs/common/components/atoms/TextField';
import DropdownField, {
  DropdownFieldAction,
} from '@tcs/common/components/atoms/DropdownField';
import CreateQuestionForm from '@tcs/client-portal/components/forms/CreateQuestionForm';
import { SECURITYQUESTIONS } from '@tcs/client-portal/assets/constants';

import { useStyles } from './style';

const validateAnswerRule = string()
  .required('Please enter your answer')
  // has any non-whitespace character
  .matches(/[\S].*/, { message: 'Answer cannot be empty' });

const validationSchema = object({
  question: string().required('Please choose a question'),
  answer: validateAnswerRule,
});

const SecurityQuestionsForm = ({
  initialValues,
  onSubmit,
  onCancel,
  showUpdateQuestionFail,
}: FormProps) => {
  const classes = useStyles();
  const [customQuestions, setCustomQuestions] = useState([]);

  const onSubmitCustomQuestion = (question: string) => {
    setCustomQuestions([...customQuestions, question]);
  };

  const onDeleteCustomQuestion = useCallback(
    (question: string) => {
      setCustomQuestions(without(customQuestions, question));
    },
    [customQuestions]
  );

  const questions = useMemo(() => {
    return [
      ...SECURITYQUESTIONS.map((question: string) => ({
        label: question,
        value: question,
      })),
      ...customQuestions.map((question: string) => ({
        label: question,
        value: question,
        action: (
          <DropdownFieldAction
            onClick={() => onDeleteCustomQuestion(question)}
            endIcon={<MinusIcon />}
          />
        ),
      })),
    ];
  }, [customQuestions, onDeleteCustomQuestion]);

  const renderQuestionField = (props: FieldRenderProps<string>) => {
    return (
      <DropdownField
        label="Question"
        options={questions}
        renderAddon={(updatePosition) => (
          <CreateQuestionForm
            onSubmit={onSubmitCustomQuestion}
            onVisible={updatePosition}
            questions={[...SECURITYQUESTIONS, ...customQuestions]}
          />
        )}
        {...props}
      />
    );
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      render={({ handleSubmit, submitting }: FormRenderProps) => (
        <form onSubmit={handleSubmit} className={classes.form}>
          <Fade
            in={true}
            timeout={750}
            style={{ display: showUpdateQuestionFail ? undefined : 'none' }}>
            <div className={classes.banner}>
              <Banner severity="error" bannerType="inline">
                <Typography
                  variant="body2"
                  color="textPrimary"
                  data-test-id="login-message">
                  Security question change was unsuccessful.
                </Typography>
              </Banner>
            </div>
          </Fade>
          <div className={classes.container}>
            <div className={classes.title}>
              <Typography variant="h5" data-test-id="sub-title">
                Change Security Question
              </Typography>
            </div>
            <div className={classes.fields}>
              <div className={classes.input} data-test-id="question">
                <Field name="question" render={renderQuestionField} />
              </div>
              <div className={classes.input}>
                <Field
                  data-test-id="answer"
                  name="answer"
                  render={(props: FieldRenderProps<string>) => (
                    <TextField
                      type="text"
                      label="Answer"
                      data-private
                      {...props}
                    />
                  )}
                />
              </div>
            </div>
            <div className={classes.buttonContainer}>
              <Button
                size="medium"
                color="secondary"
                onClick={onCancel}
                data-test-id="cancel-button">
                Cancel
              </Button>
              <Button
                className={classes.primaryButton}
                size="medium"
                color="primary"
                type="submit"
                disabled={submitting}
                data-test-id="save-button">
                Save
              </Button>
            </div>
          </div>
        </form>
      )}
    />
  );
};

export default SecurityQuestionsForm;
